import { AiSearchHost, useAskAiSearch } from '@/features/ai-search';
import { currentLoggedUserVar } from '@/shared/api';
import { sendHeartbeat } from '@/shared/api/services/activity-log';
import { ROUTES } from '@/shared/config';
import { NotificationProvider } from '@/shared/lib/context/notificationContext';
import { MessageProvider } from '@/shared/lib/context/patientMessageContext';
import { useIdleModal } from '@/shared/lib/hooks/useIdleModal';
import useSettings from '@/shared/lib/hooks/useSettings';
import { TAB_ID, useTabEngagement } from '@/shared/lib/hooks/useTabEngagement';
import useToken from '@/shared/lib/hooks/useToken';
import { Role } from '@/shared/lib/types/admin';
import { useFeatureFlagStore } from '@/shared/model';
import ActivityTracker from '@/shared/ui/components/ActivityTracker';
import AppFrame from '@/shared/ui/components/AppFrame';
import RolloutGroupPrompt from '@/shared/ui/components/RolloutGroupPrompt';
import TimezoneDetector from '@/shared/ui/components/Timezone/TimezoneDetector';
import { useReactiveVar } from '@apollo/client';
import { useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

const HEARTBEAT_INTERVAL_MS = 15000;

const Authenticated = () => {
  const { token, handleLogout, roleType } = useToken();
  const location = useLocation();
  const { id: routeId } = useParams();
  const currentUser = useReactiveVar(currentLoggedUserVar);
  const navigate = useNavigate();
  const { settings, companySettings } = useSettings();
  const { initializeFlags } = useFeatureFlagStore();
  const askAiSearch = useAskAiSearch();

  const { idleTimer } = useIdleModal({
    handleLogout
  });
  const { start: startIdleTimer } = idleTimer;
  const isTabEngaged = useTabEngagement();

  // Read by the heartbeat at send time, so navigating doesn't tear down and restart the interval.
  // Same patient check as ActivityTracker: a route `id` only counts under /patients/<id>.
  const engagedPatientId = routeId && location.pathname.startsWith(`${ROUTES.PATIENTS.ROOT}/${routeId}`) ? routeId : undefined;
  const engagedPatientIdRef = useRef(engagedPatientId);
  engagedPatientIdRef.current = engagedPatientId;

  const inactivityTimeoutSeconds = (settings['inactivityTimeoutSeconds'] as number) ?? 1;

  const idleTimerStarted = useRef(false);

  useEffect(() => {
    if (!token) {
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }

    if (location.pathname === ROUTES.ROOT) {
      navigate(ROUTES.HOME);
    }
  }, [token, location.pathname, navigate]);

  useEffect(() => {
    if (!token) {
      idleTimerStarted.current = false;
      return;
    }

    if (idleTimerStarted.current || inactivityTimeoutSeconds <= 0) {
      return;
    }

    startIdleTimer();
    idleTimerStarted.current = true;
  }, [token, inactivityTimeoutSeconds, startIdleTimer]);

  const sendBeat = useCallback(() => {
    sendHeartbeat({ patientId: engagedPatientIdRef.current, userActive: isTabEngaged(), tabId: TAB_ID }).catch(console.error);
  }, [isTabEngaged]);

  useEffect(() => {
    if (!token) {
      return;
    }

    const interval = setInterval(sendBeat, HEARTBEAT_INTERVAL_MS);
    window.addEventListener('focus', sendBeat);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', sendBeat);
    };
  }, [token, sendBeat]);

  useEffect(() => {
    if (token) {
      sendBeat();
    }
  }, [token, engagedPatientId, sendBeat]);

  const isPharmacistAwaitingAgreement = !!currentUser && !currentUser?.relyingParty?.isCtaAccepted && roleType === Role.PHARMACIST;

  useEffect(() => {
    if (isPharmacistAwaitingAgreement && location.pathname !== ROUTES.AGREEMENT) {
      navigate(ROUTES.AGREEMENT);
    }
  }, [isPharmacistAwaitingAgreement, location.pathname, navigate]);

  useEffect(() => {
    if (companySettings) {
      initializeFlags(companySettings);
    }
  }, [companySettings, initializeFlags]);

  return (
    <NotificationProvider>
      <MessageProvider>
        <ActivityTracker />
        <TimezoneDetector />
        <RolloutGroupPrompt />
        <AppFrame onAskQuestion={askAiSearch}>
          <Outlet />
        </AppFrame>
        <AiSearchHost />
      </MessageProvider>
    </NotificationProvider>
  );
};

export default Authenticated;
